import { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { timingLabel } from '../../lib/automationLabels';
import { downloadCsv } from '../../lib/csv';
import { isManagedObject, isObjectThatMatters, isSystemObject } from '../../lib/objectsThatMatter';
import { EmptyState } from '../ui/EmptyState';
import type {
  AutomationByObject,
  AutomationObjectGroup,
  AutomationSeverity,
  AutomationTech,
  ObjectsResult,
} from '../../types/org.types';

const TECH_ORDER: AutomationTech[] = [
  'Flow',
  'Process Builder',
  'Workflow Rule',
  'Apex Trigger',
  'Validation Rule',
  'Approval Process',
  'Assignment Rule',
  'Auto-Response Rule',
  'Escalation Rule',
];

type Chip = 'findings' | 'all' | 'hide-managed' | 'active-only' | 'show-all';

export function AutomationByObjectTab({
  data,
  openObject,
  scanObjects,
}: {
  data: AutomationByObject | undefined;
  openObject: string | null;
  // Stored client scan (/internal) only: its objects audit. Given, the tab
  // opens on the objects that matter with managed items hidden, a "Show all"
  // chip reveals the rest, and system objects never show.
  scanObjects?: ObjectsResult | null;
}) {
  const scan = scanObjects !== undefined;
  const [scope, setScope] = useState<'findings' | 'all'>('all');
  const [hideManaged, setHideManaged] = useState(scan);
  const [activeOnly, setActiveOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // A deep link from the top-10 card scrolls to the opened object.
  useEffect(() => {
    if (openObject) document.getElementById(`automation-${openObject}`)?.scrollIntoView({ block: 'start' });
  }, [openObject]);

  const groups = useMemo(() => {
    if (!data) return [];
    const objectByName = new Map((scanObjects?.objects ?? []).map((o) => [o.apiName, o]));
    return data.groups
      .filter((g) => {
        if (!scan || g.object === null) return true;
        if (isSystemObject(g.object)) return false;
        if (g.object === openObject) return true;
        if (hideManaged && isManagedObject(g.object)) return false;
        if (showAll) return true;
        const o = objectByName.get(g.object);
        // An object the objects audit did not list still carries automation.
        return o ? isObjectThatMatters(o, g.items.length) : true;
      })
      .map((g) => ({
        ...g,
        items: g.items
          .filter((i) => (hideManaged ? !i.managed : true))
          .filter((i) => (activeOnly ? i.active === true : true)),
      }))
      .filter((g) => g.items.length > 0)
      .filter((g) => (scope === 'findings' ? g.findings.length > 0 : true));
  }, [data, hideManaged, activeOnly, scope, scan, showAll, scanObjects, openObject]);

  if (!data) return <EmptyState title="No per-object automation in this scan." />;

  const chips: { key: Chip; label: string; on: boolean; toggle: () => void }[] = [
    { key: 'findings', label: 'With findings', on: scope === 'findings', toggle: () => setScope('findings') },
    { key: 'all', label: 'All objects', on: scope === 'all', toggle: () => setScope('all') },
    { key: 'hide-managed', label: 'Hide managed', on: hideManaged, toggle: () => setHideManaged((v) => !v) },
    { key: 'active-only', label: 'Active only', on: activeOnly, toggle: () => setActiveOnly((v) => !v) },
    ...(scan
      ? [{ key: 'show-all' as const, label: 'Show all', on: showAll, toggle: () => setShowAll((v) => !v) }]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border">
        <Tile value={data.totals.objectsWithAutomation} label="Objects with automation" />
        <Tile value={data.totals.mixedAutomation} label="Mixed automation" tone="bad" />
        <Tile value={data.totals.unorderedFlows} label="Unordered flows" tone="warn" />
        <Tile value={data.totals.inactivePileUps} label="Inactive pile-ups" />
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={c.toggle}
            className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
              c.on
                ? 'bg-crux-light border-crux-light text-cream'
                : 'bg-surface-0 border-border-strong text-neutral-400 hover:text-crux-light'
            }`}
          >
            {c.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => downloadAutomationCsv(groups)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-border-strong bg-surface-0 text-neutral-400 hover:text-crux-light transition-colors"
        >
          <Download size={13} />
          Download CSV
        </button>
      </div>

      {groups.length === 0 ? (
        <EmptyState title="No objects matched." />
      ) : (
        groups.map((g) => <ObjectRow key={g.label} group={g} openObject={openObject} />)
      )}
    </div>
  );
}

// One row per automation item, over the groups as currently filtered.
const AUTOMATION_CSV_HEADERS = [
  'Object',
  'Name',
  'Type',
  'Timing',
  'State',
  'Run order',
  'API version',
  'Managed',
];

// State is always shown as text, including the unknown case.
function stateLabel(active: boolean | null): string {
  return active === null ? 'Unknown' : active ? 'Active' : 'Inactive';
}

function downloadAutomationCsv(groups: AutomationObjectGroup[]) {
  const rows = groups.flatMap((g) =>
    g.items.map((i) => [
      g.object ?? g.label,
      i.name,
      i.type,
      timingLabel(i.timing) ?? '',
      stateLabel(i.active),
      i.runOrder ?? '',
      i.apiVersion ?? '',
      i.managed ? 'yes' : 'no',
    ]),
  );
  downloadCsv('orgiq-automation-by-object.csv', AUTOMATION_CSV_HEADERS, rows);
}

function Tile({ value, label, tone }: { value: number; label: string; tone?: 'bad' | 'warn' }) {
  const valueColor = value === 0 ? 'text-crux-light' : tone === 'bad' ? 'text-sunset' : tone === 'warn' ? 'text-sunrise' : 'text-crux-light';
  return (
    <div className="bg-surface-2 px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">{label}</div>
    </div>
  );
}

function ObjectRow({ group, openObject }: { group: AutomationObjectGroup; openObject: string | null }) {
  return (
    <details
      id={group.object ? `automation-${group.object}` : undefined}
      className="border border-border bg-surface-2"
      open={openObject !== null && group.object === openObject}
    >
      <summary className="cursor-pointer list-none px-4 py-3 grid gap-3 sm:grid-cols-[1.4fr_2fr_auto] items-center hover:bg-surface-hover">
        <div>
          <div className="text-sm font-semibold text-crux-light">{group.label}</div>
          <div className="text-[11px] font-mono text-neutral-600 tabular-nums">
            {group.activeCount} active · {group.inactiveCount} inactive
          </div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {TECH_ORDER.map((tech) => (
            <span
              key={tech}
              className={`text-[11px] font-semibold px-2 py-0.5 border border-border-strong bg-surface-0 whitespace-nowrap ${
                group.techCounts[tech] === 0 ? 'opacity-40' : ''
              }`}
            >
              {tech} <b className="font-mono tabular-nums">{group.techCounts[tech]}</b>
            </span>
          ))}
        </div>
        <SeverityChip group={group} />
      </summary>
      <div className="border-t border-border bg-surface-0 px-4 py-3">
        {group.findings.map((f) => (
          <div
            key={f.rule}
            className={`text-[13px] text-crux-light px-3 py-2 mb-1.5 border-l-[3px] ${
              f.severity === 'high'
                ? 'border-sunset bg-sunset/[0.06]'
                : f.severity === 'medium'
                  ? 'border-sunrise bg-sunrise/[0.07]'
                  : 'border-border-strong bg-surface-2'
            }`}
          >
            {f.text}
          </div>
        ))}
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left py-1.5 pr-2 font-medium">Name</th>
              <th className="text-left py-1.5 pr-2 font-medium">Type</th>
              <th className="text-left py-1.5 pr-2 font-medium">Timing</th>
              <th className="text-left py-1.5 pr-2 font-medium">State</th>
              <th className="text-right py-1.5 pr-2 font-medium">Run order</th>
              <th className="text-right py-1.5 font-medium">API</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((item) => (
              <tr key={item.id} className="border-t border-border">
                <td className="py-1.5 pr-2 text-crux-light">
                  {item.name}
                  {item.managed ? (
                    <span className="ml-2 text-[10px] uppercase font-semibold border border-border-strong px-1.5 text-neutral-500">
                      managed
                    </span>
                  ) : null}
                </td>
                <td className="py-1.5 pr-2 text-neutral-400">{item.type}</td>
                <td className="py-1.5 pr-2 text-neutral-400">{timingLabel(item.timing) ?? ''}</td>
                <td
                  className={`py-1.5 pr-2 font-semibold ${item.active === true ? 'text-grow' : 'text-neutral-600'}`}
                >
                  {stateLabel(item.active)}
                </td>
                <td className="py-1.5 pr-2 text-right text-neutral-400 tabular-nums">
                  {item.runOrder ?? (isRecordTriggeredFlow(item.type, item.timing) ? 'not set' : '')}
                </td>
                <td className="py-1.5 text-right text-neutral-400 tabular-nums">{item.apiVersion ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

const SEVERITY_LABEL: Record<AutomationSeverity, string> = { high: 'High', medium: 'Medium', low: 'Low' };

function SeverityChip({ group }: { group: AutomationObjectGroup }) {
  if (group.object === null) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-grow/40 text-grow bg-grow/10 whitespace-nowrap">
        Not scored
      </span>
    );
  }
  if (!group.severity) {
    return <span className="text-[10px] uppercase tracking-wider text-neutral-600">No findings</span>;
  }
  const tone =
    group.severity === 'high'
      ? 'text-sunset border-sunset/40 bg-sunset/10'
      : group.severity === 'medium'
        ? 'text-sunrise border-sunrise/40 bg-sunrise/10'
        : 'text-neutral-500 border-border-strong';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border whitespace-nowrap ${tone}`}>
      {SEVERITY_LABEL[group.severity]} · {group.findings.length} finding{group.findings.length === 1 ? '' : 's'}
    </span>
  );
}

// Run order only applies to record-triggered flows, so "not set" is shown
// for those rows only and every other row keeps an empty cell.
function isRecordTriggeredFlow(type: AutomationTech, timing: string | null): boolean {
  return type === 'Flow' && Boolean(timing) && timing!.startsWith('Record');
}
